/**
 * Polls a saved estimate's render job while there is something to wait for.
 *
 * Every 3 s (the server's own `pollMs`) at first; 10 s once the page has been
 * open five minutes; 15 s, and a soft "checking on your render" line, after
 * three failed polls in a row; 15 s too while a queued job has waited over a
 * quarter of an hour with no worker taking it ("rendering is delayed"). Paused
 * while the tab is hidden, with a poll the moment it is shown again. Stops for
 * good at done, failed, no job at all, or an estimate that is gone (404/410).
 */

import { useEffect, useRef, useState } from 'react';
import { fetchRenderStatus } from '../../../services/api';
import type { RenderStatus } from './types';

export type RenderStatusFetcher = (guid: string, signal: AbortSignal) => Promise<RenderStatus>;

export interface RenderStatusState {
  status: RenderStatus | null;
  /** 'gone': the estimate is deleted or unknown (polling stopped); 'network': the last poll failed (still trying). */
  error: 'gone' | 'network' | null;
  /** Three polls in a row failed: the page says it is checking rather than freezing a stale line. */
  checking: boolean;
  /** Queued for over 15 minutes with no worker taking it. */
  delayed: boolean;
  /** Nothing more will come: done, failed, no job, or gone. */
  settled: boolean;
}

export const POLL_FAST_MS = 3000;
export const POLL_SLOW_MS = 10000;
export const POLL_TROUBLE_MS = 15000;
export const SLOW_AFTER_MS = 5 * 60 * 1000;
export const DELAYED_AFTER_MS = 15 * 60 * 1000;
export const FAILS_BEFORE_CHECKING = 3;

/** True when there is nothing more to poll for. */
export function isSettled(status: RenderStatus): boolean {
  const s = status.job?.status;
  return !status.job || s === 'done' || s === 'failed';
}

/** Queued (or still awaiting its upload) for longer than anyone should wait unannounced. */
export function isDelayed(status: RenderStatus, now: number): boolean {
  const job = status.job;
  if (!job || (job.status !== 'queued' && job.status !== 'awaiting-upload') || !job.createdAt) return false;
  const created = Date.parse(job.createdAt);
  return Number.isFinite(created) && now - created > DELAYED_AFTER_MS;
}

/** How long to wait before the next poll after a good answer. */
export function nextDelay(status: RenderStatus, elapsedMs: number, now: number): number {
  const base = status.pollMs > 0 ? status.pollMs : POLL_FAST_MS;
  if (isDelayed(status, now)) return Math.max(base, POLL_TROUBLE_MS);
  if (elapsedMs > SLOW_AFTER_MS) return Math.max(base, POLL_SLOW_MS);
  return base;
}

const INITIAL: RenderStatusState = { status: null, error: null, checking: false, delayed: false, settled: false };

export function useRenderStatus(guid: string | null | undefined, enabled = true, fetcher: RenderStatusFetcher = fetchRenderStatus): RenderStatusState {
  const [state, setState] = useState<RenderStatusState>(INITIAL);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!guid || !enabled) {
      setState(INITIAL);
      return undefined;
    }
    let stopped = false;
    let settled = false;
    let hidden = false;
    let failures = 0;
    let timer: number | null = null;
    let controller: AbortController | null = null;
    const startedAt = Date.now();

    const schedule = (ms: number) => {
      if (stopped || settled) return;
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => { void poll(); }, ms);
    };

    const poll = async () => {
      if (stopped || settled) return;
      if (typeof document !== 'undefined' && document.hidden) { hidden = true; return; } // resumed on visibilitychange
      controller = new AbortController();
      try {
        const status = await fetcherRef.current(guid, controller.signal);
        if (stopped) return;
        failures = 0;
        settled = isSettled(status);
        const now = Date.now();
        setState({ status, error: null, checking: false, delayed: isDelayed(status, now), settled });
        if (!settled) schedule(nextDelay(status, now - startedAt, now));
      } catch (e) {
        if (stopped || (e instanceof Error && e.name === 'AbortError')) return;
        const code = (e as { status?: number }).status;
        if (code === 404 || code === 410) {
          settled = true;
          setState((s) => ({ ...s, error: 'gone', checking: false, settled: true }));
          return;
        }
        failures += 1;
        const checking = failures >= FAILS_BEFORE_CHECKING;
        setState((s) => ({ ...s, error: 'network', checking }));
        schedule(checking ? POLL_TROUBLE_MS : POLL_FAST_MS);
      }
    };

    const onVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden && hidden) { hidden = false; void poll(); }
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    void poll();

    return () => {
      stopped = true;
      if (timer !== null) window.clearTimeout(timer);
      controller?.abort();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [guid, enabled]);

  return state;
}
